import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RenderMonitor from '../components/RenderMonitor';
import { supabase } from '../utils/supabase';
import { testReflectionFollowUp } from '../utils/reflectionFollowUp';

function ReflectionAITestPage({ courseId, questionId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [courseName, setCourseName] = useState('');
  const [lessonLabel, setLessonLabel] = useState('');
  const [question, setQuestion] = useState(null);

  const [responseText, setResponseText] = useState('');
  const [category, setCategory] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [model, setModel] = useState('');

  const categories = Array.isArray(question?.categories) ? question.categories : [];

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const parsedCourseId = parseInt(courseId, 10);
        const parsedQuestionId = parseInt(questionId, 10);

        const [{ data: courseData, error: courseError }, { data: questionRow, error: questionError }] = await Promise.all([
          supabase.from('courses').select('id, name').eq('id', parsedCourseId).single(),
          supabase
            .from('questions')
            .select('id, order, lesson_id, lessons(lesson_id, title, position, course_id)')
            .eq('id', parsedQuestionId)
            .single(),
        ]);

        if (!mounted) return;
        if (courseError) {
          setLoadError(courseError.message || 'Failed to load course.');
          return;
        }
        if (questionError || !questionRow) {
          setLoadError('Reflection question not found.');
          return;
        }

        const lesson = Array.isArray(questionRow.lessons) ? questionRow.lessons[0] : questionRow.lessons;
        if (!lesson || Number(lesson.course_id) !== parsedCourseId) {
          setLoadError('This reflection question does not belong to this course.');
          return;
        }

        const { data: reflectionData, error: reflectionError } = await supabase
          .from('reflection_questions')
          .select('question_id, prompt_text, extra_details, reflection_question_categories(course_categories(id, category_name))')
          .eq('question_id', parsedQuestionId)
          .single();

        if (!mounted) return;
        if (reflectionError || !reflectionData) {
          setLoadError('Failed to load reflection question details.');
          return;
        }

        const cats = (reflectionData.reflection_question_categories || [])
          .map((c) => c.course_categories)
          .filter((c) => c?.id && c?.category_name)
          .map((c) => ({ id: c.id, category_name: c.category_name }));

        const lessonTitle = lesson.title ? `: ${lesson.title}` : '';
        setCourseName(courseData?.name || 'Course');
        setLessonLabel(`Lesson ${lesson.position || ''}${lessonTitle}`);
        setQuestion({
          id: reflectionData.question_id,
          order: questionRow.order,
          question_text: reflectionData.prompt_text || '',
          extra_details: reflectionData.extra_details || '',
          categories: cats,
        });
        setCategory(cats[0]?.category_name || '');
        setResponseText('');
        setError('');
        setResult('');
        setModel('');
      } catch (e) {
        if (!mounted) return;
        setLoadError(e?.message || 'Failed to load reflection question.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [courseId, questionId]);

  const handleGenerate = async () => {
    setError('');
    setResult('');
    setModel('');
    const reflectionText = (responseText || '').trim();
    if (!reflectionText) {
      setError('Enter a sample learner response.');
      return;
    }
    setGenerating(true);
    try {
      const data = await testReflectionFollowUp({
        courseId,
        reflectionQuestionId: question.id,
        reflectionPrompt: question.question_text,
        extraDetails: question.extra_details,
        reflectionText,
        category: categories.length > 0 ? category : undefined,
      });
      setResult(data?.follow_up_question || '');
      setModel(data?.model || '');
      if (!data?.follow_up_question) {
        setError('No follow-up question returned.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to generate follow-up question.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="modern-loading">Loading test page…</div>
      </div>
    );
  }

  if (loadError || !question) {
    return (
      <div className="dashboard-page">
        <div className="modern-error">{loadError || 'Reflection question not found.'}</div>
        <button
          type="button"
          className="modern-button modern-button-secondary modern-mt-4"
          onClick={() => navigate(`/course/${courseId}`)}
        >
          ← {courseName || 'Course'}
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="ReflectionAITestPage" />

      <button
        type="button"
        className="modern-button modern-button-secondary modern-mb-6"
        onClick={() => navigate(`/course/${courseId}`)}
        disabled={generating}
      >
        ← {courseName || 'Course'}
      </button>

      <div className="modern-card modern-mb-6">
        <h1 className="modern-title" style={{ marginBottom: 4 }}>Test AI Follow-Up</h1>
        <p className="modern-subtitle" style={{ marginBottom: 0 }}>
          {lessonLabel} · Apply Q{question.order || ''}
        </p>
        <p style={{ margin: '8px 0 0 0', color: '#94a3b8', fontSize: 13 }}>
          Preview the generated follow-up question. Nothing is saved to the database.
        </p>
      </div>

      <div className="modern-card">
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>Reflection prompt</div>
          <div style={{ padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#334155' }}>
            {question.question_text}
          </div>
        </div>

        {(question.extra_details || '').trim() ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>Extra details</div>
            <div style={{ padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#334155', whiteSpace: 'pre-wrap' }}>
              {question.extra_details}
            </div>
          </div>
        ) : null}

        {categories.length > 0 ? (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Category (optional)</label>
            <select
              className="modern-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={generating}
              style={{ width: '100%' }}
            >
              {categories.map((cat) => (
                <option key={cat.id || cat.category_name} value={cat.category_name}>
                  {cat.category_name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Sample learner response</label>
          <textarea
            className="modern-textarea"
            rows={6}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            disabled={generating}
            placeholder="Write a sample answer as if you were the learner..."
            style={{ width: '100%', resize: 'vertical', minHeight: 140 }}
          />
        </div>

        {error ? <div className="modern-error" style={{ marginBottom: 12 }}>{error}</div> : null}

        {result ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>
              AI follow-up question{model ? ` (${model})` : ''}
            </div>
            <div style={{ padding: 12, borderRadius: 8, border: '1px solid #c4b5fd', background: '#f5f3ff', color: '#334155', whiteSpace: 'pre-wrap' }}>
              {result}
            </div>
          </div>
        ) : null}

        <button
          type="button"
          className="modern-button modern-button-success"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Generating…' : 'Generate follow-up'}
        </button>
      </div>
    </div>
  );
}

export default ReflectionAITestPage;
